/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, Typography } from '@mui/material';
import React from 'react';
import type { layerTypes } from '@opengeoweb/store';
import DatasetRowConnect from './DatasetRowConnect';
import type {
  DiscoveryMetadataFeature,
  DiscoveryMetadataItems,
} from '../../store/types';
import { useDataExplorerTranslation } from '../../utils/i18n';

interface DatasetListProps {
  datasets?: DiscoveryMetadataItems | null;
  mapLayers: layerTypes.Layer[];
  searchFilter: string;
}

const DatasetList: React.FC<DatasetListProps> = ({
  datasets,
  mapLayers,
  searchFilter,
}: DatasetListProps) => {
  const { t } = useDataExplorerTranslation();
  if (!datasets || datasets.features.length === 0) {
    return (
      <Typography sx={{ marginLeft: '6px' }}>
        {t('dataexplorer-no-data')}
      </Typography>
    );
  }

  return (
    <Grid sx={{ overflowY: 'hidden', overflowX: 'hidden', height: '100%' }}>
      <Typography
        color="textSecondary"
        sx={{ marginLeft: '6px' }}
        variant="caption"
      >
        {`${datasets?.features.length} results`}
      </Typography>
      <Grid
        size={12}
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          height: 'calc(100% - 28px)',
          paddingRight: '8px',
          containerType: 'inline-size',
        }}
      >
        {datasets.features.map((feature: DiscoveryMetadataFeature) => {
          return (
            <DatasetRowConnect
              feature={feature}
              key={feature.id}
              mapLayers={mapLayers}
              searchFilter={searchFilter}
            />
          );
        })}
      </Grid>
    </Grid>
  );
};

export default DatasetList;
